import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Button, TextField } from '@mui/material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadParameter,
  CLEAR_PARAMETER
} from 'src/store/actions/parameterActions';
import { Parameter } from 'src/utils/constants/parameters';
import { selectIndividualParameter } from 'src/store/selectors/parameterSelector';
import useStyles from './styles';

const TerminationAuditToolbar = ({ className, config, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const parameterTolerance = useSelector(selectIndividualParameter);
  const [tolerance, setTolerance] = useState('');
  const [toleranceInitialized, setToleranceInitialized] = useState(false);

  useEffect(() => {
    dispatch(loadParameter(Parameter.PAYMENT_TOLERANCE_AMOUNT));
    dispatch(config.actions.load());
    return () => {
      dispatch(config.actions.clear());
      dispatch({ type: CLEAR_PARAMETER });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (
      !toleranceInitialized &&
      parameterTolerance !== null &&
      parameterTolerance !== undefined
    ) {
      setTolerance(String(Number(parameterTolerance)));
      setToleranceInitialized(true);
    }
  }, [parameterTolerance, toleranceInitialized]);

  const handleToleranceChange = useCallback(event => {
    setTolerance(event.target.value);
  }, []);

  const handleSearch = useCallback(() => {
    // No limit/offset sent on purpose — the backend returns the full,
    // uncapped offender list and TerminationAuditList paginates it
    // client-side, so re-searching never re-triggers a full portfolio
    // schedule recomputation per page.
    dispatch(
      config.actions.load({
        tolerance: Number(tolerance) || 0
      })
    );
  }, [dispatch, config.actions, tolerance]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Breadcrumb />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography style={{ fontWeight: '400' }} color="primary" variant="h1">
          {t(config.titleKey)}
        </Typography>
        <div className={classes.actionsContainer}>
          <TextField
            label={t('CAPITAL_BALANCE.AUDIT_TOLERANCE_LABEL')}
            type="number"
            size="small"
            value={tolerance}
            onChange={handleToleranceChange}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{
              '& .MuiInputBase-root': {
                height: 40,
                backgroundColor: 'background.paper'
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            sx={{
              height: 40,
              padding: '0 20px',
              borderRadius: '4px',
              borderColor: 'primary.main',
              color: 'primary.main',
              backgroundColor: 'background.paper',
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.5px',
              '&:hover': {
                backgroundColor: 'background.paper',
                borderColor: 'primary.main',
                opacity: 0.8
              }
            }}
          >
            {t('COMMON.APPLY')}
          </Button>
        </div>
      </div>
    </div>
  );
};

TerminationAuditToolbar.propTypes = {
  className: PropTypes.string,
  config: PropTypes.shape({
    actions: PropTypes.shape({
      load: PropTypes.func.isRequired,
      clear: PropTypes.func.isRequired
    }).isRequired,
    titleKey: PropTypes.string.isRequired
  }).isRequired
};

export default TerminationAuditToolbar;
